// @ts-nocheck
import "./App.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { lazy, useEffect, useState, useCallback, ComponentType } from "react";
import { ToastContainer } from "react-bootstrap";

import {  Route,  Routes,  useLocation,  Navigate,  useNavigate,} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { LoadingProvider } from "./components/LoadingContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import useInterceptor from "./api/apiService";
import ScrollToTop from "./components/loaders/ScrollToTop";
import { GlobalLoader } from "./components/GlobalLoader";
import HeadNavBar from "./layout/HeadNavBar";
import Footer from "./layout/Footer";
import { isJwtValid, readAuthFromCookies } from "./utilities/authUtils";
import { getOrCreateRandomUserId } from "./utilities/cookieUtils";
import { fetchCartWishlistCounts } from "./api/cartWishlistSyncService";
import { setCounts } from "./store/apiCountsSlice";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import AuthModal from "./components/modals/AuthModal";
import WishlistModal from "./components/modals/WishlistModal";
import CartDrawer from "./components/cart/CartDrawer";
import SplashCursor from "./components/SplashCursor";
// import ThemeToggle from "./components/ThemeToggle";
// import ThemeSelector from "./components/ThemeSelector";
import useLenis from "./hooks/useLenis";

const ForgetPassword = lazy(() => import("./pages/ForgetPassword"));
const HomePage = lazy(() => import("./pages/HomePage"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Cart = lazy(() => import("./pages/Cart"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const CheckoutAddres = lazy(() => import("./pages/CheckoutAddres"));
const Order = lazy(() => import("./pages/Order"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const SellerStore = lazy(() => import("./pages/SellerStore"));
const BrandStore = lazy(() => import("./pages/BrandStore"));
const VideoTest = lazy(() => import("./pages/VideoTest"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const PricePage = lazy(() => import("./pages/PricePage"));
const MovementPage = lazy(() => import("./pages/MovementPage"));
const ErrorPage = lazy(() => import("./pages/ErrorPage"));

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  useInterceptor();

  // Initialize random_user cookie on app load
  useEffect(() => {
    const randomUserId = getOrCreateRandomUserId();
  }, []);

  // ✅ #1 OPTIMIZATION: Fetch cart & wishlist counts ONCE at app level
  // This prevents duplicate fetches from HomePage and HeadNavBar
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const userId = auth?.userInfo?.user_id || auth?.userInfo?.guest_id;
        const userType = auth?.userInfo?.user_id ? "user" : "guest";

        if (userId) {
          const counts = await fetchCartWishlistCounts(userId, userType);
          dispatch(setCounts({ cartCount: counts.cartCount, wishlistCount: counts.wishlistCount }));
        }
      } catch (error) {
        // Silent fail - counts will update from other sources
      }
    };

    if (auth?.userInfo?.user_id || auth?.userInfo?.guest_id) {
      fetchCounts();
    }
  }, [auth?.userInfo?.user_id, auth?.userInfo?.guest_id, dispatch]);

  // Validate JWT on every route change. If invalid, clear tokens and redirect to login for protected pages.
  useEffect(() => {
    const { jwt } = readAuthFromCookies();
    const token = jwt || localStorage.getItem("JWT");
    const valid = isJwtValid(token);
    const pathname = location.pathname || "/";

    // publicPaths allowed without auth
    const publicPaths = [
      "/",
      "/login",
      "/register",
      "/forgetpassword",
      "/cart",
      "/search",
      "/invoice",
      "/demo-invoice",
    ];

    if (!valid && !publicPaths.some((p) => pathname.startsWith(p))) {
      // Not valid and trying to access protected route -> redirect to login
      localStorage.removeItem("JWT");
      localStorage.removeItem("x-auth");
      navigate("/login");
    }
  }, [location]);

  // Hide header/footer on specific routes. Login, Register, Forgot Password
  // and Cart are no longer standalone pages — visiting them just opens a
  // modal/drawer over the home page, so the layout stays visible.
  const noLayoutPaths = [
    "/checkout/address",
    "/checkout/payment",
    "/invoice",
    "/demo-invoice",
    "/error",
  ];
  const pathname = location.pathname || "";
  const showLayout = !noLayoutPaths.some((p) => pathname.startsWith(p));

  return (
    <>
      <ToastContainer />
      <CookieConsent />
      <AuthModal />
      <WishlistModal />
      <CartDrawer />

      {/* Header (fixed) - render on all pages except Cart */}
      {showLayout && (
        <div className="fixed-head-wrapper">
          <HeadNavBar />
        </div>
      )}

      {/* Main content - add padding when header/footer present */}
      <main className={`main-content ${showLayout ? "with-layout" : ""}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgetpassword" element={<ForgetPassword />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/checkout/cart" element={<Cart />} />
          <Route path="/address" element={<CheckoutAddres />} />
          <Route path="/order" element={<Order />} />
          <Route path="/orders" element={<Order />} />
          <Route path="/product/:productName/:code" element={<ProductPage />} />
          <Route path="/search/category/:categoryName" element={<SearchResults />} />
          <Route path="/search/product/:searchQuery" element={<SearchResults />} />
          <Route path="/global-loader" element={<GlobalLoader />} />
          <Route path="/seller/:id" element={<SellerStore />} />
          <Route path="/brand/:id" element={<BrandStore />} />
          {/* <Route path="/video-test" element={<VideoTest />} /> */}
          <Route path="/collection/:collectionSlug" element={<CollectionPage />} />
          <Route path="/price/:priceSlug" element={<PricePage />} />
          <Route path="/movement/:movementSlug" element={<MovementPage />} />
          <Route
            path="*"
            element={
              <ErrorPage
                title="Page Not Found"
                message="The page you're looking for doesn't exist or has been moved."
              />
            }
          />
        </Routes>
      </main>

      {/* Footer (fixed) - render on all pages except Cart */}
      {showLayout && (
        <div className="fixed-footer-wrapper">
          <Footer />
        </div>
      )}
    </>
  );
}

function App() {
  useLenis();

  const [AgentationComp, setAgentationComp] = useState<ComponentType<any> | null>(null);
  const [loaded, setLoaded] = useState(false);

  const handleDone = useCallback(() => setLoaded(true), []);

 

  return (
    <ThemeProvider>
      <div className="App">
        <ErrorBoundary>
          <LoadingProvider>
            <ScrollToTop />
            {AgentationComp ? <AgentationComp /> : null}
            <GlobalLoader onComplete={handleDone} />
            <div style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.5s ease' }}>
              <AppContent />
            </div>
          </LoadingProvider>
        </ErrorBoundary>
      </div>
    </ThemeProvider>
  );
}

export default App;
